import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { LinkCard } from '@eika/link-panel';
import { Modal } from '@eika/modal';

import useText from '@/hooks/use-text';
import { useClick2PayProfile } from '@/react-query/hooks/query/use-click2pay';
import { useClick2PayIsAllowed } from '@/react-query/hooks/useClick2PayIsAllowed';

import { useSession } from '@components/app';
import SkeletonList from '@components/skeleton/SkeletonList';
import { useConfigLink } from '@domain/app';
import '@pages/click2pay/Click2Pay.scss';
import { getClick2PayUrl } from '@pages/click2pay/functions';
import click2PayRoutes from '@pages/click2pay/routes';

const Click2PayLinkCard = () => {
    const text = useText();
    const navigate = useNavigate();
    const routes = click2PayRoutes;
    const [queryParams] = useSearchParams();
    const { highAssurance } = useSession();
    const link = useConfigLink('stepUpAuthLink', { url: '/' });
    const [showModal, setShowModal] = useState(false);
    const click2PayIsAllowed = useClick2PayIsAllowed();

    const profile = useClick2PayProfile();

    if (!click2PayIsAllowed) {
        return null;
    }

    if (profile.isPending) {
        return <SkeletonList count={1} />;
    }

    const handleClick2Pay = (route: string) => {
        if (!highAssurance) {
            setShowModal(true);
            return;
        }

        navigate(getClick2PayUrl(queryParams, routes[route]));
    };

    return (
        <div className="click-2-pay">
            {profile.data?.customerProfile ? (
                <LinkCard
                    icon={IconTypes.BEVEGELSER_24}
                    title={text.click2PayTitle}
                    textTwo={text.click2PayChangeSettingsHere}
                    onClick={() => handleClick2Pay(routes.admin)}
                />
            ) : (
                <LinkCard
                    icon={IconTypes.BEVEGELSER_24}
                    title="Nå kan du bruke Click to Pay fra Visa"
                    textTwo="Netthandel gjort lekende lett"
                    onClick={() => handleClick2Pay(routes.onboarding)}
                />
            )}

            {showModal && (
                <Modal
                    title={text.click2PayTitle}
                    open
                    setOpen={setShowModal}
                    onClose={() => {
                        setShowModal(false);
                        navigate('/');
                    }}
                >
                    <AttentionMessage
                        type="warning"
                        id="google-pay-low-assurance"
                        text={text.click2PayLowAssuranceText}
                        icon={IconTypes.INFO_24}
                        clickableItem={<Link href={link.url}>{text.click2PayGetGoing}</Link>}
                    />
                </Modal>
            )}
        </div>
    );
};

export default Click2PayLinkCard;
